/**
 * useSavedViews — the stateful half of Slice D / PR D2.
 *
 * Owns: the fetched SavedView[] for one list surface, the LIVE ViewState the
 * list renders from, which saved view (if any) is active, and the drift dropped
 * when the active view was opened (A2). The list page drives filters/columns/
 * sort through the setters here; the SavedViewsBar drives apply/save/default/
 * delete. All persistence is per-user + tenant-scoped server-side (D1).
 *
 * On mount the user's DEFAULT view for this surface is applied automatically —
 * this is the platform's first per-user preference (A1). Views are private;
 * nothing here shares them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { apiErrorMessage } from '../../../../api/client'
import { fetchSavedViews, createSavedView, updateSavedView, deleteSavedView } from '../../api'
import {
  type ListSchema, type SavedView, type ViewState, type DroppedItem,
  baseState, reconcileView, stateToBody, stateEquals,
} from './savedViews'

function rawBody(schema: ListSchema, v: SavedView, isDefault: boolean) {
  // Resend a view's STORED blobs unchanged (used for the default toggle) so
  // flipping default never silently rewrites or self-heals the saved shape.
  return {
    object: schema.object,
    name: v.name,
    filters: (v.filters ?? {}) as Record<string, string>,
    columns: Array.isArray(v.columns) ? (v.columns as string[]) : [],
    ...(v.sort && typeof v.sort.field === 'string'
      ? { sort: { field: v.sort.field, dir: v.sort.dir === 'desc' ? 'desc' as const : 'asc' as const } }
      : {}),
    isDefault,
  }
}

export interface UseSavedViews {
  loading: boolean
  views: SavedView[]
  activeId: string | null
  activeName: string | null
  state: ViewState
  drift: DroppedItem[]
  isModified: boolean
  setFilter: (key: string, value: string) => void
  setColumns: (columns: string[]) => void
  setSort: (sort: ViewState['sort']) => void
  applyView: (id: string | null) => void
  saveNew: (name: string, asDefault: boolean) => Promise<void>
  updateActive: () => Promise<void>
  setDefault: (id: string, isDefault: boolean) => Promise<void>
  remove: (id: string) => Promise<void>
  busy: boolean
}

export function useSavedViews(schema: ListSchema): UseSavedViews {
  const [views, setViews] = useState<SavedView[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [drift, setDrift] = useState<DroppedItem[]>([])
  const [state, setState] = useState<ViewState>(() => baseState(schema))

  const reload = useCallback(async (): Promise<SavedView[]> => {
    const list = await fetchSavedViews(schema.object)
    setViews(list)
    return list
  }, [schema])

  // Initial load: fetch, then apply the default view if there is one. `loading`
  // already starts true, so the effect only ever flips it false when settled —
  // no synchronous setState in the effect body.
  useEffect(() => {
    let live = true
    fetchSavedViews(schema.object)
      .then(list => {
        if (!live) return
        setViews(list)
        const def = list.find(v => v.isDefault)
        if (def) {
          const { state: s, dropped } = reconcileView(def, schema)
          setState(s); setActiveId(def.id); setDrift(dropped)
        }
      })
      .catch(() => { /* views are a convenience; a fetch failure leaves the base list usable */ })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [schema])

  const setFilter = useCallback((key: string, value: string) => {
    setState(s => ({ ...s, filters: { ...s.filters, [key]: value } }))
  }, [])
  const setColumns = useCallback((columns: string[]) => {
    setState(s => ({ ...s, columns }))
  }, [])
  const setSort = useCallback((sort: ViewState['sort']) => {
    setState(s => ({ ...s, sort }))
  }, [])

  const applyView = useCallback((id: string | null) => {
    if (id === null) {
      setState(baseState(schema)); setActiveId(null); setDrift([])
      return
    }
    const v = views.find(x => x.id === id)
    if (!v) return
    const { state: s, dropped } = reconcileView(v, schema)
    setState(s); setActiveId(id); setDrift(dropped)
  }, [views, schema])

  const active = useMemo(() => views.find(v => v.id === activeId) ?? null, [views, activeId])

  // Modified = the live state differs from the active view's reconciled state.
  // No active view (base) is never "modified" — there is nothing to update.
  const isModified = useMemo(() => {
    if (!active) return false
    return !stateEquals(state, reconcileView(active, schema).state)
  }, [active, state, schema])

  const saveNew = useCallback(async (name: string, asDefault: boolean) => {
    const trimmed = name.trim()
    if (!trimmed) { toast.error('Name this view first'); return }
    setBusy(true)
    try {
      const created = await createSavedView(stateToBody(schema, trimmed, state, asDefault))
      await reload()
      setActiveId(created.id); setDrift([])
      toast.success(`View “${created.name}” saved`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not save this view'))
    } finally { setBusy(false) }
  }, [schema, state, reload])

  const updateActive = useCallback(async () => {
    if (!active) return
    setBusy(true)
    try {
      await updateSavedView(active.id, stateToBody(schema, active.name, state, active.isDefault))
      await reload()
      setDrift([]) // re-saving reconciled state clears any prior drift
      toast.success(`View “${active.name}” updated`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not update this view'))
    } finally { setBusy(false) }
  }, [active, schema, state, reload])

  const setDefault = useCallback(async (id: string, isDefault: boolean) => {
    const v = views.find(x => x.id === id)
    if (!v) return
    setBusy(true)
    try {
      await updateSavedView(id, rawBody(schema, v, isDefault))
      await reload()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not change the default'))
    } finally { setBusy(false) }
  }, [views, schema, reload])

  const remove = useCallback(async (id: string) => {
    setBusy(true)
    try {
      await deleteSavedView(id)
      const list = await reload()
      if (activeId === id) {
        // fall back to another default if one remains, else the base list
        const def = list.find(v => v.isDefault)
        if (def) { const r = reconcileView(def, schema); setState(r.state); setActiveId(def.id); setDrift(r.dropped) }
        else { setState(baseState(schema)); setActiveId(null); setDrift([]) }
      }
      toast.success('View deleted')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not delete this view'))
    } finally { setBusy(false) }
  }, [activeId, schema, reload])

  return {
    loading, views, activeId, activeName: active?.name ?? null, state, drift, isModified,
    setFilter, setColumns, setSort, applyView, saveNew, updateActive, setDefault, remove, busy,
  }
}
